import React from 'react';
import {
  BarChart, Bar, PieChart, Pie, Cell, LineChart, Line,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
} from 'recharts';
import { AlertCircle, ExternalLink } from 'lucide-react';
import { PIE_COLORS, TAXE_BADGE } from './widgetCatalog';
import { fmtEuro, reportUrl, matchSlice, matchRow, selKey } from './analysesUtils';

// Petit hook utilitaire : ctx peut être absent (mode aperçu).
const asCtx = (ctx) => ctx || {};

const KpiCard = ({ label, value, sub, highlight, onClick, active }) => (
  <div
    onClick={onClick}
    className={`rounded-xl border p-4 transition-all duration-150 ${onClick ? 'cursor-pointer hover:shadow-md hover:-translate-y-0.5' : ''} ${active ? 'ring-2 ring-[#397DFF] border-[#397DFF]' : ''} ${highlight ? 'bg-gradient-to-br from-[#F97316] to-[#FB923C] border-orange-200 text-white' : 'bg-white border-[#BFC6CC]'}`}
    data-testid={onClick ? `kpi-clickable-${label}` : undefined}>
    <div className={`text-xs uppercase tracking-wider ${highlight ? 'text-white/80' : 'text-[#687175]'}`}>{label}</div>
    <div className={`text-2xl font-semibold mt-1 ${highlight ? 'text-white' : 'text-[#191D1E]'}`}>{value}</div>
    <div className={`text-xs mt-1 ${highlight ? 'text-white/80' : 'text-[#687175]'}`}>{sub}</div>
  </div>
);

const KpiWidget = ({ data, ctx }) => {
  const { toggleSelect, selectedKeys } = asCtx(ctx);
  const isActive = (dim, id) => selectedKeys?.has(selKey(dim, id));
  return (
    <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="widget-kpis">
      <KpiCard label="Total dossiers" value={data.nb_total} sub={`${data.nb_ticfe} TICFE · ${data.nb_ticgn} TICGN · ${data.nb_turpe} TURPE`} />
      <KpiCard label="Économies totales" value={fmtEuro(data.total_economies)} sub="Cumulées" highlight />
      <KpiCard label="Taux d'éligibilité" value={`${data.taux_eligibilite}%`} sub={`${data.nb_eligibles} dossiers éligibles`}
        onClick={toggleSelect ? () => toggleSelect({ dim: 'eligibilite', id: 'ELIGIBLE', label: 'Éligibles' }) : undefined}
        active={isActive('eligibilite', 'ELIGIBLE')} />
      <KpiCard label="Validés client" value={`${data.nb_valides_client || 0}`} sub={`${fmtEuro(data.eco_valides_client || 0)} (${data.taux_validation || 0}%)`}
        onClick={toggleSelect ? () => toggleSelect({ dim: 'valide_client', id: 'oui', label: 'Validés client' }) : undefined}
        active={isActive('valide_client', 'oui')} />
      <KpiCard label="Économie moyenne" value={fmtEuro(data.economie_moyenne)} sub="Par dossier" />
    </div>
  );
};

const HistoWidget = ({ data, ctx }) => {
  const { toggleSelect, byDim } = asCtx(ctx);
  const selMois = byDim?.mois;
  const onCellClick = (mois) => { if (toggleSelect && mois) toggleSelect({ dim: 'mois', id: mois, label: mois }); };
  const opacityFor = (mois) => (selMois && selMois.size > 0 ? (selMois.has(String(mois)) ? 1 : 0.3) : 1);
  return (
    <ResponsiveContainer width="100%" height={280}>
      <BarChart data={data}>
        <CartesianGrid strokeDasharray="3 3" stroke="#F5F6F9" />
        <XAxis dataKey="mois" stroke="#687175" fontSize={11} />
        <YAxis stroke="#687175" fontSize={11} />
        <Tooltip contentStyle={{ borderRadius: 8, fontSize: 12 }} cursor={{ fill: 'rgba(57,125,255,0.06)' }} />
        <Legend />
        {['TICFE', 'TICGN', 'TURPE'].map((k, ki) => (
          <Bar key={k} dataKey={k} stackId="a" fill={['#397DFF', '#F97316', '#0891B2'][ki]} isAnimationActive={false}>
            {data.map((d, i) => (
              <Cell key={i} fillOpacity={opacityFor(d.mois)}
                style={{ cursor: toggleSelect ? 'pointer' : 'default' }}
                onClick={() => onCellClick(d.mois)} />
            ))}
          </Bar>
        ))}
      </BarChart>
    </ResponsiveContainer>
  );
};

const EmptyState = ({ message = 'Aucune donnée disponible pour ce widget.' }) => (
  <div className="flex items-center justify-center py-10 text-sm text-[#687175]">
    <AlertCircle className="w-4 h-4 mr-2" /> {message}
  </div>
);

const PieWidget = ({ data, ctx }) => {
  if (!data?.length) return <EmptyState />;
  const { byDim, toggleSelect, commToAgence, selectedKeys } = asCtx(ctx);
  return (
    <ResponsiveContainer width="100%" height={280}>
      <PieChart>
        <Pie data={data} dataKey="value" nameKey="label" cx="50%" cy="50%" outerRadius={90}
          isAnimationActive={false}
          label={(e) => `${e.label || 'Non attribué'} ${(e.percent * 100).toFixed(0)}%`}>
          {data.map((item, i) => {
            const rel = matchSlice(item.dim, item.id, byDim, commToAgence);
            const opacity = rel === false ? 0.25 : 1;
            const active = item.dim && selectedKeys?.has(selKey(item.dim, item.id));
            return (
              <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} fillOpacity={opacity}
                stroke={active || rel === true ? '#191D1E' : undefined} strokeWidth={active || rel === true ? 2 : undefined}
                style={{ cursor: item.dim && toggleSelect ? 'pointer' : 'default' }}
                onClick={() => { if (item.dim && toggleSelect) toggleSelect({ dim: item.dim, id: String(item.id), label: item.label }); }} />
            );
          })}
        </Pie>
        <Tooltip formatter={(v) => fmtEuro(v)} contentStyle={{ borderRadius: 8, fontSize: 12 }} />
      </PieChart>
    </ResponsiveContainer>
  );
};

const LineWidget = ({ data, ctx }) => {
  const { toggleSelect, byDim } = asCtx(ctx);
  const selMois = byDim?.mois;
  const onClick = (e) => {
    if (toggleSelect && e && e.activeLabel) toggleSelect({ dim: 'mois', id: e.activeLabel, label: e.activeLabel });
  };
  return (
    <ResponsiveContainer width="100%" height={280}>
      <LineChart data={data} onClick={onClick} style={{ cursor: toggleSelect ? 'pointer' : 'default' }}>
        <CartesianGrid strokeDasharray="3 3" stroke="#F5F6F9" />
        <XAxis dataKey="mois" stroke="#687175" fontSize={11} />
        <YAxis stroke="#687175" fontSize={11} tickFormatter={(v) => `${Math.round(v / 1000)}k`} />
        <Tooltip formatter={(v) => fmtEuro(v)} contentStyle={{ borderRadius: 8, fontSize: 12 }} />
        <Line type="monotone" dataKey="economies" stroke="#10B981" strokeWidth={2.5}
          isAnimationActive={false}
          dot={(props) => {
            const sel = selMois && selMois.size > 0 && selMois.has(String(props.payload.mois));
            return <circle key={props.payload.mois} cx={props.cx} cy={props.cy} r={sel ? 6 : 4}
              fill={sel ? '#191D1E' : '#10B981'} stroke="#fff" strokeWidth={1} />;
          }} />
      </LineChart>
    </ResponsiveContainer>
  );
};

const HeatmapWidget = ({ data, ctx }) => {
  if (!data?.rows?.length) return <EmptyState />;
  const { byDim, toggleSelect, commToAgence } = asCtx(ctx);
  const max = Math.max(1, ...data.rows.flatMap(r => data.mois.map(m => r[m] || 0)));
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-xs">
        <thead>
          <tr>
            <th className="text-left p-2 text-[#687175] font-medium">Commercial</th>
            {data.mois.map(m => (
              <th key={m} className="p-2 text-center text-[#687175] font-medium whitespace-nowrap cursor-pointer hover:text-[#397DFF]"
                onClick={() => { if (toggleSelect) toggleSelect({ dim: 'mois', id: m, label: m }); }}>
                {m}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {data.rows.map((row, i) => {
            const rel = matchSlice('commercial', row.commercial_id, byDim, commToAgence);
            const rowOpacity = rel === false ? 0.3 : 1;
            return (
              <tr key={i} style={{ opacity: rowOpacity, cursor: toggleSelect ? 'pointer' : 'default' }}
                className={rel === true ? 'ring-2 ring-[#397DFF]' : ''}
                onClick={() => { if (toggleSelect && row.commercial_id) toggleSelect({ dim: 'commercial', id: String(row.commercial_id), label: row.commercial }); }}>
                <td className="p-2 text-[#191D1E] font-medium whitespace-nowrap">{row.commercial}</td>
                {data.mois.map(m => {
                  const v = row[m] || 0;
                  const intensity = v / max;
                  const bg = v === 0 ? '#FAFBFC' : `rgba(57, 125, 255, ${0.1 + 0.7 * intensity})`;
                  return (
                    <td key={m} className="p-2 text-center font-medium" style={{ backgroundColor: bg, color: intensity > 0.5 ? 'white' : '#191D1E' }}>
                      {v || ''}
                    </td>
                  );
                })}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

const DossiersACloturerWidget = ({ data }) => {
  if (!data?.length) return <EmptyState message="Aucun dossier à clôturer dans les 6 prochains mois." />;
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="bg-[#FAFBFC]">
          <tr>
            <th className="text-left p-2 text-[#687175] font-medium">Client</th>
            <th className="text-left p-2 text-[#687175] font-medium">SIREN</th>
            <th className="text-center p-2 text-[#687175] font-medium">Taxe</th>
            <th className="text-right p-2 text-[#687175] font-medium">Économies</th>
            <th className="text-center p-2 text-[#687175] font-medium">Échéance CERFA</th>
            <th className="text-center p-2 text-[#687175] font-medium">Jours restants</th>
          </tr>
        </thead>
        <tbody>
          {data.map((r, i) => (
            <tr key={i} className="border-t border-[#F5F6F9]">
              <td className="p-2 text-[#191D1E]">{r.nom_entreprise}</td>
              <td className="p-2 text-[#687175]">{r.siren}</td>
              <td className="p-2 text-center"><span className="px-2 py-0.5 rounded text-xs bg-[#FFEDD5] text-[#9A3412]">{r.taxe}</span></td>
              <td className="p-2 text-right font-medium">{fmtEuro(r.economies)}</td>
              <td className="p-2 text-center text-xs">{r.cerfa_echeance}</td>
              <td className={`p-2 text-center font-semibold ${r.jours_restants < 30 ? 'text-red-600' : r.jours_restants < 90 ? 'text-orange-600' : 'text-[#191D1E]'}`}>
                {r.jours_restants} j
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const ListeDossiersWidget = ({ data, ctx }) => {
  if (!data?.length) return <EmptyState message="Aucun dossier ne correspond aux filtres." />;
  const { byDim, commToAgence } = asCtx(ctx);
  return (
    <div className="overflow-x-auto max-h-[480px] overflow-y-auto" data-testid="widget-liste-dossiers-table">
      <table className="w-full text-sm">
        <thead className="bg-[#FAFBFC] sticky top-0">
          <tr>
            <th className="text-left p-2 text-[#687175] font-medium">Client</th>
            <th className="text-left p-2 text-[#687175] font-medium">SIREN</th>
            <th className="text-center p-2 text-[#687175] font-medium">Taxe</th>
            <th className="text-right p-2 text-[#687175] font-medium">Économies</th>
            <th className="text-center p-2 text-[#687175] font-medium">Éligibilité</th>
            <th className="text-center p-2 text-[#687175] font-medium">Validé</th>
            <th className="text-center p-2 text-[#687175] font-medium">Ouvrir</th>
          </tr>
        </thead>
        <tbody>
          {data.map((r, i) => {
            const rel = matchRow(r, byDim, commToAgence);
            const url = reportUrl(r);
            return (
              <tr key={i}
                className={`border-t border-[#F5F6F9] ${rel === true ? 'bg-blue-50' : ''}`}
                style={{ opacity: rel === false ? 0.4 : 1 }}>
                <td className="p-2 text-[#191D1E]">{r.nom_entreprise}</td>
                <td className="p-2 text-[#687175]">{r.siren}</td>
                <td className="p-2 text-center"><span className={`px-2 py-0.5 rounded text-xs ${TAXE_BADGE[r.taxe] || 'bg-gray-100 text-gray-700'}`}>{r.taxe}</span></td>
                <td className="p-2 text-right font-medium">{fmtEuro(r.economies)}</td>
                <td className="p-2 text-center text-xs">{r.eligibilite || '—'}</td>
                <td className="p-2 text-center">{r.valide_client ? <span className="text-green-600">✓</span> : <span className="text-[#BFC6CC]">—</span>}</td>
                <td className="p-2 text-center">
                  {url ? (
                    <button
                      onClick={() => window.open(url, '_blank', 'noopener')}
                      className="inline-flex items-center gap-1 text-[#397DFF] hover:underline text-xs"
                      data-testid={`open-dossier-${r.taxe}-${i}`}
                      title="Ouvrir le rapport dans un nouvel onglet">
                      <ExternalLink className="w-3.5 h-3.5" /> Voir
                    </button>
                  ) : '—'}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

export const WIDGET_RENDERERS = {
  kpis: KpiWidget,
  dossiers_par_mois: HistoWidget,
  economies_par_type: PieWidget,
  economies_par_agence: PieWidget,
  economies_par_commercial: PieWidget,
  evolution_economies: LineWidget,
  heatmap_commerciaux_mois: HeatmapWidget,
  dossiers_a_cloturer: DossiersACloturerWidget,
  liste_dossiers: ListeDossiersWidget,
};
